import { Link } from "react-router-dom";

export default function Terms() {
  return (
    <main className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-16" data-testid="terms-page">
      <p className="label-eyebrow mb-2">Terms</p>
      <h1 className="font-serif text-4xl sm:text-5xl leading-tight mb-3">Terms of use</h1>
      <p className="text-sm" style={{ color: "var(--text-2)" }}>
        Last updated {new Date().toLocaleDateString(undefined, { dateStyle: "long" })}
      </p>

      <Section title="What Alternavit is, and isn't">
        Alternavit is an educational tool that suggests natural supplements that <em>may</em> support common wellness
        concerns based on the answers you give. It is <strong>not</strong> a medical service, a medical device, a
        diagnosis, or a substitute for the advice, examination, or treatment of a qualified healthcare professional.
        Always consult one before starting a new supplement.
      </Section>

      <Section title="Your account">
        You must be at least 18 years old to use Alternavit. You agree to provide accurate information and to keep
        your password confidential. You are responsible for everything that happens under your account. If you think
        someone else has access to it, let us know immediately at <a className="underline" style={{ color: "var(--brand)" }} href="mailto:support@alternavit.com">support@alternavit.com</a>.
      </Section>

      <Section title="Acceptable use">
        Don&apos;t:
        <ul className="list-disc pl-5 space-y-2 mt-3">
          <li>Use Alternavit for any unlawful purpose or to harm others.</li>
          <li>Reverse-engineer, scrape, or rate-limit-evade the service.</li>
          <li>Impersonate someone else or misrepresent your relationship with them.</li>
          <li>Use our content (including AI-generated explanations) to provide medical advice to third parties.</li>
        </ul>
      </Section>

      <Section title="Health and safety disclaimer">
        Information shown in the app, in emails, in PDF exports, and via the share-with-practitioner link is provided
        for general informational and educational purposes only. We make no warranty as to the accuracy,
        completeness, or fitness for any particular purpose of any recommendation. <strong>You are solely
        responsible</strong> for any decision to take a supplement based on information from Alternavit, and you
        agree to consult a qualified healthcare professional first.
      </Section>

      <Section title="Affiliate links">
        Some links on Alternavit go to third-party retailers (such as iHerb, Amazon, Holland &amp; Barrett, MyProtein,
        and others). If you click a link and make a purchase, Alternavit may receive a small commission at no extra
        cost to you. This never affects which supplements we recommend &mdash; recommendations are driven by your
        questionnaire answers, not by commission rates.
      </Section>

      <Section title="Intellectual property">
        The Alternavit brand, the supplement database structure, the questionnaire, the AI prompt design, and the
        design system are owned by us. Your own assessment data and tracker logs belong to you. We grant you a
        non-transferable, revocable licence to use the service for personal, non-commercial purposes.
      </Section>

      <Section title="Subscription, payments, and refunds">
        Alternavit is currently free to use. If we introduce paid features, terms specific to them (including
        refunds and cancellation) will be displayed at the point of purchase and incorporated into these Terms.
      </Section>

      <Section title="Termination">
        You can delete your account at any time from <Link to="/settings" className="underline" style={{ color: "var(--brand)" }}>Settings</Link>. We may suspend or terminate accounts that violate these terms or pose a risk to others. We&apos;ll let you know by email unless legal or safety reasons prevent it.
      </Section>

      <Section title="Limitation of liability">
        To the maximum extent permitted by law, Alternavit and its operators are not liable for indirect, incidental,
        consequential, or punitive damages arising out of your use of the service. Our total liability to you for any
        claim is limited to the amount you have paid us in the 12 months preceding the claim (in many cases, zero).
        Nothing in these terms excludes liability that cannot be excluded under EU consumer law.
      </Section>

      <Section title="Governing law and disputes">
        These Terms are governed by the laws of Ireland and, where applicable, the consumer-protection laws of your
        country of residence within the EU. You may bring disputes before the courts of your country of residence or
        of Ireland.
      </Section>

      <Section title="Contact">
        Privacy questions: <a className="underline" style={{ color: "var(--brand)" }} href="mailto:privacy@alternavit.com">privacy@alternavit.com</a>.<br />
        Anything else: <a className="underline" style={{ color: "var(--brand)" }} href="mailto:support@alternavit.com">support@alternavit.com</a>.
      </Section>

      <p className="mt-12 text-sm">
        <Link to="/" className="underline" style={{ color: "var(--brand)" }}>← Back to home</Link>
      </p>
    </main>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-serif text-2xl mb-3">{title}</h2>
      <div className="leading-relaxed" style={{ color: "var(--text-2)" }}>{children}</div>
    </section>
  );
}
